import { useEffect, useRef } from "react";
import { removeProcessingRatedAgeOCRJob } from "reducers/rated-age-ocr";
import { api } from "services/api";
import { useGetBackgroundJobByIdQuery } from "services/background-job-api";
import type { RootState } from "services/store";
import { TERMINAL_BACKGROUND_JOB_STATUSES } from "utils/backgroundJobStatus";
import { useDispatch, useSelector } from "utils/redux";

import KaboodleToaster from "./KaboodleToaster";

// Deliberately not RATED_AGE_JOB_POLL_INTERVAL_MS, since this watcher
// runs in the background across the whole app, and the backend poller
// that actually finishes these jobs only runs every 3 minutes anyway.
const WATCHER_POLL_INTERVAL_MS = 15000;

type SingleJobWatcherProps = {
  jobId: number;
};

const SingleRatedAgeOCRJobWatcher = ({ jobId }: SingleJobWatcherProps) => {
  const dispatch = useDispatch();
  // Stops Strict Mode's double-invoked effects from toasting twice.
  const hasHandledTerminalStatus = useRef(false);

  const { data: job } = useGetBackgroundJobByIdQuery(jobId, {
    pollingInterval: WATCHER_POLL_INTERVAL_MS,
  });

  useEffect(() => {
    if (!job) return;
    if (!TERMINAL_BACKGROUND_JOB_STATUSES.includes(job.status)) return;
    if (hasHandledTerminalStatus.current) return;
    hasHandledTerminalStatus.current = true;

    if (job.status === "Success") {
      // All four are registered tags in api.ts, same ones rated-age-api.ts
      // already invalidates on manual create/update/delete.
      dispatch(api.util.invalidateTags(["RatedAge", "MSA", "MCP", "LCP"]));
      KaboodleToaster.success("Rated age response parsed successfully.");
    } else {
      KaboodleToaster.error(
        job.status_message || "Failed to parse the rated age response.",
      );
    }

    dispatch(removeProcessingRatedAgeOCRJob(jobId));
  }, [job, jobId, dispatch]);

  return null;
};

// Mounted once at the app root so uploads keep being watched even after
// the modal closes.
const RatedAgeOCRWatcher = () => {
  const processingJobIds = useSelector(
    (state: RootState) => state.ratedAgeOCR.processingJobIds,
  );

  return (
    <>
      {processingJobIds.map((jobId) => (
        <SingleRatedAgeOCRJobWatcher key={jobId} jobId={jobId} />
      ))}
    </>
  );
};

export default RatedAgeOCRWatcher;
